import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight, Boxes, MapPin, Globe2, Building2, Package, Target, Eye, Check,
  Calculator, Banknote, AlertTriangle,
} from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { MarketingHero, SectionHead, GradientCta, fadeUp } from "../components/marketing/Blocks";

const TRUST = [
  { icon: Boxes, label: "Multiple Courier Partners" },
  { icon: MapPin, label: "Pan-India Reach" },
  { icon: Globe2, label: "Domestic & International" },
  { icon: Building2, label: "Built for Businesses" },
];

const WHAT = [
  { icon: Boxes, title: "Multi-Courier Shipping", desc: "Access multiple courier options from one platform." },
  { icon: Calculator, title: "Smart Courier Selection", desc: "Choose shipping services based on price, serviceability and delivery requirements." },
  { icon: MapPin, title: "Real-Time Tracking", desc: "Monitor shipment movement from pickup to delivery." },
  { icon: Banknote, title: "COD Management", desc: "Manage cash-on-delivery shipments and settlements efficiently." },
  { icon: AlertTriangle, title: "NDR Management", desc: "Take action on delivery exceptions and reduce avoidable RTOs." },
  { icon: Globe2, title: "International Shipping", desc: "Simplify cross-border shipping with integrated logistics workflows." },
];

const WHY = [
  "One platform for multiple shipping requirements",
  "Transparent and easy-to-understand workflows",
  "Technology-driven logistics management",
  "Designed for businesses of every size",
  "Scalable infrastructure",
  "Powerful automation capabilities",
];

function HeroVisual() {
  return (
    <div className="relative mx-auto mt-12 aspect-[16/10] max-w-lg sm:aspect-[16/9]" data-testid="about-hero-visual">
      <div className="absolute inset-0 flex items-center justify-center">
        <img src="/maps/india.svg" alt="ShipAny delivery routes across India" className="h-full w-auto opacity-60" />
      </div>
      <svg viewBox="0 0 100 62" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
        <path d="M 18 42 Q 34 16 52 30 T 82 20" fill="none" stroke="#12C8F5" strokeWidth="0.6" vectorEffect="non-scaling-stroke" className="route-dash" />
        <path d="M 22 46 Q 42 52 60 42 T 80 34" fill="none" stroke="#FF9933" strokeWidth="0.6" vectorEffect="non-scaling-stroke" className="route-dash" style={{ animationDuration: "7s" }} />
        <path d="M 30 36 Q 48 24 66 38" fill="none" stroke="#10B981" strokeWidth="0.6" vectorEffect="non-scaling-stroke" className="route-dash" style={{ animationDuration: "9s" }} />
      </svg>
      <motion.div animate={{ scale: [1, 1.06, 1] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-white/20 bg-white p-3 shadow-[0_18px_50px_rgba(3,11,24,0.6)]">
        <img src="/logo.png" alt="ShipAny" className="h-10 w-10 object-contain" />
      </motion.div>
      <motion.div animate={{ left: ["18%", "78%"], top: ["42%", "22%"] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", repeatType: "mirror" }}
        className="absolute flex h-10 w-10 items-center justify-center rounded-xl border border-[#FF9933]/50 bg-[#071B3A] shadow-[0_6px_20px_rgba(255,153,51,0.4)]"
        data-testid="about-hero-package">
        <Package className="h-4 w-4 text-[#FF9933]" />
      </motion.div>
      {[[24, 46], [52, 28], [76, 24], [60, 44]].map(([x, y], i) => (
        <span key={i} className="pulse-dot absolute h-2.5 w-2.5 rounded-full bg-shipany-cyan" style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${i * 0.5}s` }} />
      ))}
    </div>
  );
}

export default function About() {
  return (
    <MarketingLayout>
      <div data-testid="about-page">
        <MarketingHero kicker="About ShipAny" title="Shipping Technology Built for Growing Businesses"
          sub="ShipAny brings multiple courier services, intelligent shipping tools, tracking, COD management and logistics automation together in one powerful platform.">
          <motion.div {...fadeUp} className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link to="/signup" data-testid="about-start-shipping-btn"
              className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#FF9933] to-[#FF8000] px-8 py-3.5 font-display text-sm font-bold text-[#071B3A] transition-all hover:shadow-[0_8px_30px_rgba(255,153,51,0.45)]">
              Start Shipping <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" data-testid="about-talk-btn"
              className="flex items-center justify-center gap-2 rounded-xl border border-white/20 px-8 py-3.5 font-display text-sm font-bold text-white transition-colors hover:bg-white/5">
              Talk to Our Team
            </Link>
          </motion.div>
          <HeroVisual />
        </MarketingHero>

        <section className="border-y border-white/10 bg-[#071B3A] py-10" data-testid="about-trust-strip">
          <div className="mx-auto grid max-w-6xl grid-cols-2 gap-4 px-5 lg:grid-cols-4">
            {TRUST.map((t, i) => (
              <motion.div key={t.label} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`about-trust-${t.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="flex items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-5">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#FF9933]/15"><t.icon className="h-4 w-4 text-[#FF9933]" /></span>
                <span className="text-xs font-bold text-white sm:text-sm">{t.label}</span>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="bg-shipany-navy py-20 sm:py-24" data-testid="about-story-section">
          <div className="mx-auto max-w-3xl px-5">
            <SectionHead kicker="Our Story" title="Making Shipping Simple, Smart & Accessible" sub="Shipping should not be complicated." />
            <motion.div {...fadeUp} className="mt-10 space-y-5">
              <p className="text-sm leading-relaxed text-slate-300 sm:text-base">
                ShipAny is built to give businesses a single platform to manage their shipping operations — from comparing courier options and creating shipments to tracking deliveries, handling COD and managing delivery exceptions.
              </p>
              <p className="text-sm leading-relaxed text-slate-300 sm:text-base">
                Whether you're a growing D2C brand shipping a few orders a day or an established business processing shipments at scale, ShipAny provides the technology and flexibility to simplify your logistics workflow.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="bg-[#071B3A] py-20 sm:py-24" data-testid="about-what-section">
          <div className="mx-auto max-w-7xl px-5">
            <SectionHead kicker="The platform" title="What ShipAny Does" />
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {WHAT.map((f, i) => (
                <motion.div key={f.title} {...fadeUp} transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                  data-testid={`about-what-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-shipany-cyan/40">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-shipany-blue/40 to-shipany-cyan/20 transition-transform duration-300 group-hover:scale-110">
                    <f.icon className="h-5 w-5 text-shipany-cyan" />
                  </span>
                  <h3 className="font-display mt-4 text-sm font-bold text-white">{f.title}</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{f.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-shipany-navy py-20 sm:py-24" data-testid="about-mission-vision-section">
          <div className="mx-auto grid max-w-5xl gap-5 px-5 md:grid-cols-2">
            <motion.div {...fadeUp} className="rounded-3xl border border-[#FF9933]/30 bg-[#FF9933]/5 p-8" data-testid="about-mission-card">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FF9933]/15"><Target className="h-5 w-5 text-[#FF9933]" /></span>
              <h3 className="font-display mt-5 text-xl font-bold text-white">Our Mission</h3>
              <p className="mt-3 font-display text-lg font-semibold text-[#FF9933]">"Make logistics simpler for every business."</p>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">We believe businesses should spend their time growing their brands — not managing complicated shipping operations.</p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ duration: 0.55, delay: 0.1 }} className="rounded-3xl border border-[#10B981]/30 bg-[#10B981]/5 p-8" data-testid="about-vision-card">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#10B981]/15"><Eye className="h-5 w-5 text-[#10B981]" /></span>
              <h3 className="font-display mt-5 text-xl font-bold text-white">Our Vision</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">To build a smarter logistics ecosystem where businesses can ship faster, operate efficiently and deliver better customer experiences.</p>
            </motion.div>
          </div>
        </section>

        <section className="bg-[#071B3A] py-20 sm:py-24" data-testid="about-why-section">
          <div className="mx-auto max-w-5xl px-5">
            <SectionHead kicker="Why ShipAny" title="Why Businesses Choose ShipAny" />
            <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {WHY.map((w, i) => (
                <motion.div key={w} {...fadeUp} transition={{ duration: 0.45, delay: (i % 3) * 0.07 }}
                  data-testid={`about-why-${w.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-5 py-4">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#10B981]/15">
                    <Check className="h-4 w-4 text-[#10B981]" />
                  </span>
                  <span className="text-sm font-semibold text-slate-200">{w}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <GradientCta title="Ready to Ship Smarter?" sub="Create your ShipAny account and simplify your logistics operations."
          primary={["Start Shipping", "/signup"]} secondary={["Talk to Our Team", "/contact"]} testId="about-final-cta" />
      </div>
    </MarketingLayout>
  );
}
